/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Interactive widget action helpers — the JS side of the Android widget buttons
 * (Accept/Reject an order, Start/Pause/Resume/Break/Stop a timer). They call the
 * SAME backend endpoints the app uses, through the shared authenticated
 * `apiFetch` client, so a tap on the home-screen widget performs real work
 * without opening the app.
 *
 * The timer calls go through the widget timer FACADE (`/api/widgets/timer/*`,
 * backend `widgets.controller` + `widgetTimer.service`) rather than the per-kind
 * project / work-order endpoints. The facade:
 *   - resolves the work order's own `restaurantId` server-side, so the widget
 *     never has to send an `x-restaurant-id` scope header (the old path did, and
 *     silently failed whenever the snapshot carried no restaurant id);
 *   - resolves the CALLER's currently running / paused timer for
 *     pause|resume|break|stop, so those four take no target at all;
 *   - answers `{ timer: <state|null> }` built by the very same `getActiveTimer`
 *     the `/widgets/summary` aggregator uses, so an action response and the next
 *     snapshot fetch can never drift.
 *
 * (iOS runs the equivalent calls natively from its AppIntents in
 * `targets/widgets/WidgetAPI.swift`; this module is the Android/JS counterpart.)
 *
 * Every action is guarded and returns `{ ok, timer }` so the caller (the widget
 * task handler) can repaint from the server truth and refresh regardless of
 * outcome. Nothing in here ever throws.
 */

import { apiFetch } from '../WorkSheetContext';
import { pauseWorkday, resumeWorkday, startWorkday, stopWorkday } from '../workdayApi';

/** Default minutes promised to the customer when accepting a pending order. */
const DEFAULT_ACCEPT_MINUTES = 25;

/** Base path of the widget timer facade (backend `widgets.controller`). */
const TIMER_BASE = '/widgets/timer';

/**
 * @typedef {object} WidgetTimerState
 * @property {'project'|'workOrder'} kind        Tracked entity kind.
 * @property {string}                id          Project / work-order id.
 * @property {string}                title       Back-compat headline.
 * @property {string|null}           projectTitle Project / work-order eyebrow line.
 * @property {string|null}           taskId      Tracked task id.
 * @property {string|null}           taskTitle   Tracked task headline.
 * @property {string|null}           customerName Customer / client, when known.
 * @property {string|null}           startedAt   ISO start of the CURRENT segment (null when paused).
 * @property {'running'|'break'|'paused'} state  Authoritative widget state.
 * @property {string|null}           segmentType WORK|BREAK|COFFEE_TIME|ADMIN|TRAVEL (work orders only).
 * @property {number|null}           workSeconds Accumulated tracked work seconds.
 * @property {number|null}           breakSeconds Accumulated tracked break seconds.
 * @property {boolean}               canBreak    Break is a tracked segment type here.
 * @property {boolean}               canPause    Pause is offered here.
 */

/**
 * @typedef {object} WidgetActionResult
 * @property {boolean}                 ok    Whether the backend call succeeded.
 * @property {WidgetTimerState|null}   timer The server's post-mutation timer state.
 */

/**
 * Accept a pending order (status → accepted).
 * @param {string} orderId Mongo `_id` of the order.
 * @returns {Promise<{ ok: boolean }>} `{ ok: false }` on any failure — never throws.
 */
export const acceptOrder = async (orderId) => {
    if (!orderId) {
        return { ok: false };
    }
    try {
        await apiFetch(`/order/${orderId}/status/accepted?orderMinutes=${DEFAULT_ACCEPT_MINUTES}`);

        return { ok: true };
    } catch (_error) {
        return { ok: false };
    }
};

/**
 * Reject / cancel a pending order (status → cancelled).
 * @param {string} orderId Mongo `_id` of the order.
 * @param {string} [reason] Rejection reason recorded on the order.
 * @returns {Promise<{ ok: boolean }>} `{ ok: false }` on any failure — never throws.
 */
export const rejectOrder = async (orderId, reason = 'Widget') => {
    if (!orderId) {
        return { ok: false };
    }
    try {
        await apiFetch(`/order/${orderId}/status/cancelled?rejectReason=${encodeURIComponent(reason)}`);

        return { ok: true };
    } catch (_error) {
        return { ok: false };
    }
};

/**
 * Mark one mail message as read (`PATCH /mail/messages/:id/flags`) — the mail widget's "✓" chip. Uses the very same
 * client call as the app's reader and the notification "Mark read" action (`mailNotifications`). The mail client is
 * required lazily so the headless widget task only loads it when this chip is actually tapped.
 * @param {string} messageId Mail message id.
 * @returns {Promise<{ ok: boolean }>} `{ ok: false }` on any failure — never throws.
 */
export const markMailRead = async (messageId) => {
    if (!messageId) {
        return { ok: false };
    }
    try {
        // eslint-disable-next-line global-require
        const { updateMailMessageFlags } = require('../../services/mailApi');
        await updateMailMessageFlags(String(messageId), { isRead: true });

        return { ok: true };
    } catch (_error) {
        return { ok: false };
    }
};

/**
 * POST one widget timer facade call and normalise its `{ timer }` envelope.
 * Shared by all five timer actions so the guard/never-throw discipline and the
 * result shape live in exactly one place.
 *
 * @param {'start'|'pause'|'resume'|'break'|'stop'} segment Facade sub-path.
 * @param {object} [body] JSON body (`{}` for the target-less actions).
 * @returns {Promise<WidgetActionResult>} Never throws; `{ ok: false, timer: null }` on failure.
 */
const postTimerAction = async (segment, body = {}) => {
    try {
        const response = await apiFetch(`${TIMER_BASE}/${segment}`, {
            method: 'POST',
            body: JSON.stringify(body),
        });

        return { ok: true, timer: response && response.timer ? response.timer : null };
    } catch (_error) {
        // 404 (nothing running / paused) and 409 (genuine conflict) both land
        // here. The caller republishes a fresh snapshot either way, so the widget
        // self-corrects to whatever the server actually holds.
        return { ok: false, timer: null };
    }
};

/**
 * Start a timer on a project or work order.
 *
 * @param {{ kind: 'project'|'workOrder', id: string, taskId?: string }} target
 *   Quick-start target as published in the snapshot's `trackables[]`. Any legacy
 *   `restaurantId` on the object is ignored: the facade resolves the work order's
 *   own tenant server-side.
 * @returns {Promise<WidgetActionResult>} Never throws.
 */
export const startTimer = async (target) => {
    if (!target || !target.id) {
        return { ok: false, timer: null };
    }

    const body = {
        kind: target.kind === 'project' ? 'project' : 'workOrder',
        id: String(target.id),
    };
    if (target.taskId) {
        body.taskId = String(target.taskId);
    }

    return postTimerAction('start', body);
};

/**
 * Pause the caller's running timer. Pause is UNTRACKED dead time: the backend
 * closes the running segment / entry exactly as a stop does and stamps
 * `pausedAt` on it, so no duration or invoicing math changes — the row simply
 * becomes resumable.
 *
 * Takes no target: the server resolves whatever the caller is running.
 * @returns {Promise<WidgetActionResult>} Never throws.
 */
export const pauseTimer = async () => postTimerAction('pause', {});

/**
 * Resume the caller's paused timer (or return from a break). Opens a NEW segment
 * / entry on the same project + task and clears `pausedAt` on the row it resumed
 * from. Takes no target.
 * @returns {Promise<WidgetActionResult>} Never throws.
 */
export const resumeTimer = async () => postTimerAction('resume', {});

/**
 * Switch the caller's running work-order timer to a TRACKED break segment.
 * Unlike pause, a break IS counted (it rolls into
 * `timeStats.totalBreakTimeSeconds`). Work orders only — a project caller gets a
 * 409 from the facade, which surfaces here as `{ ok: false }`. Takes no target.
 * @returns {Promise<WidgetActionResult>} Never throws.
 */
export const breakTimer = async () => postTimerAction('break', {});

/**
 * Stop the caller's timer. Works from `running`, `break` AND `paused` (stopping
 * while paused clears `pausedAt`, otherwise a paused widget would offer Resume
 * forever). Takes no target — the legacy `target` argument is accepted and
 * ignored for back-compat with older snapshots' click payloads.
 * @returns {Promise<WidgetActionResult>} Never throws.
 */
export const stopTimer = async () => postTimerAction('stop', {});

/**
 * Run one workday-clock call and report the outcome. The workday has its own endpoints (`/workday/*`), so these three
 * widget verbs are separate from the task-timer facade above; the headless task republishes a fresh snapshot afterwards
 * either way (a 409 means the day was already ended / paused elsewhere, and the repaint shows the truth).
 *
 * @param {Function} call `workdayApi` function.
 * @param {object} [options] Options forwarded to the call.
 * @returns {Promise<{ ok: boolean }>} Never throws.
 */
const runWorkdayAction = async (call, options = {}) => {
    try {
        await call({ source: 'widget', ...options });

        return { ok: true };
    } catch (_error) {
        return { ok: false };
    }
};

/**
 * Pause the caller's workday (opens a break; worked time freezes). Idempotent server-side.
 * @returns {Promise<{ ok: boolean }>} Never throws.
 */
export const pauseWorkdayClock = async () => runWorkdayAction(pauseWorkday);

/**
 * Resume the caller's paused workday (closes the break). Idempotent server-side.
 * @returns {Promise<{ ok: boolean }>} Never throws.
 */
export const resumeWorkdayClock = async () => runWorkdayAction(resumeWorkday);

/**
 * End the caller's workday (works from running AND paused). The running task timer is left alone.
 * @returns {Promise<{ ok: boolean }>} Never throws.
 */
export const stopWorkdayClock = async () => runWorkdayAction(stopWorkday, { stopTimers: false });

/**
 * Start the caller's workday (idempotent server-side: an already running day is simply reported back).
 * @returns {Promise<{ ok: boolean }>} Never throws.
 */
export const startWorkdayClock = async () => runWorkdayAction(startWorkday);
